import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const ThemeContext = createContext({
  theme: "auto",
  effective: "light",
  setTheme: () => {},
});

const applyTheme = (effective) => {
  const root = document.documentElement;
  // Guarantee smooth cross-fade whenever the theme changes.
  root.classList.add("tf-theme-transition");
  root.setAttribute("data-theme", effective);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", effective === "dark" ? "#0B0B0F" : "#E2001A");
  // Remove the transition class after the crossfade completes so it doesn't
  // interfere with subsequent unrelated transitions (like transforms).
  window.clearTimeout(applyTheme._t);
  applyTheme._t = window.setTimeout(() => {
    root.classList.remove("tf-theme-transition");
  }, 380);
};

const getSystemTheme = () =>
  window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => localStorage.getItem("tf_theme") || "auto");
  const [systemTheme, setSystemTheme] = useState(() =>
    typeof window !== "undefined" ? getSystemTheme() : "light",
  );

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemTheme(e.matches ? "dark" : "light");
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  const effective = theme === "auto" ? systemTheme : theme;

  useEffect(() => {
    applyTheme(effective);
  }, [effective]);

  const setTheme = useCallback((next) => {
    localStorage.setItem("tf_theme", next);
    setThemeState(next);
  }, []);

  const value = useMemo(() => ({ theme, effective, setTheme }), [theme, effective, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
